import { useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Switch, Typography } from '@mui/material';
import { useTranslations } from '@/i18n/use-translations';
import type { TranslationKey } from '@/i18n/translation-key';
import { useNotifications } from '@/hooks/use-notifications';
import NumberField from '@/components/common/number-field';
import { useSetConfidenceQaMutation } from '@/state/queries/player/use-confidence';
import { resolveSectionQa, qaNotice, type QaChoice } from './confidence-qa.helper';

type Props = {
  open: boolean;
  onClose: () => void;
  entityId: string;
  slotId: string;
  sectionTitle: string;
  flow: QaChoice;
  overrides: Record<string, QaChoice>;
  originId?: string;
  fullMinutes?: number;
};

export function ConfidenceQaDialog({
  open,
  onClose,
  entityId,
  slotId,
  sectionTitle,
  flow,
  overrides,
  originId,
  fullMinutes,
}: Props) {
  const t = useTranslations();
  const notification = useNotifications();
  const setQa = useSetConfidenceQaMutation();
  const current = resolveSectionQa(flow, overrides, slotId, originId);
  const [enabled, setEnabled] = useState(current.enabled);
  const [minutes, setMinutes] = useState(current.minutes >= 1 && current.minutes <= 30 ? current.minutes : 5);

  const storedMinutes = minutes >= 1 && minutes <= 30 ? minutes : 5;
  const notice = qaNotice({ enabled, minutes: storedMinutes }, fullMinutes);

  const save = () => {
    void setQa
      .mutateAsync({
        entityType: 'Flow',
        entityId,
        slotId,
        enabled,
        minutes: storedMinutes,
      })
      .then(onClose, () =>
        notification.notifyError(t('flows.detail.control.confidence.actionError' as TranslationKey)),
      );
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{sectionTitle}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="body2">{t('flows.detail.control.confidence.qa' as TranslationKey)}</Typography>
            <Switch
              checked={enabled}
              slotProps={{
                input: { 'aria-label': t('flows.detail.control.confidence.qa' as TranslationKey) },
              }}
              onChange={(_, checked) => {
                setEnabled(checked);
                if (checked && (minutes < 1 || minutes > 30)) setMinutes(5);
              }}
            />
          </Stack>
          <NumberField
            size="small"
            label={t('flows.detail.control.confidence.qaMinutes' as TranslationKey)}
            helperText={t('flows.detail.control.confidence.qaRange' as TranslationKey)}
            min={1}
            max={30}
            value={minutes}
            disabled={!enabled || setQa.isPending}
            onValueChange={(value) => {
              if (value == null || value < 1 || value > 30) return;
              setMinutes(value);
            }}
          />
          {notice ? (
            <Typography variant="body2" color={notice === 'error' ? 'error.main' : 'warning.main'}>
              {t(
                (notice === 'error'
                  ? 'flows.detail.control.confidence.qaNone'
                  : 'flows.detail.control.confidence.qaShort') as TranslationKey,
              )}
            </Typography>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel' as TranslationKey)}</Button>
        <Button variant="contained" disabled={setQa.isPending} onClick={save}>
          {t('common.save' as TranslationKey)}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
